import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './client.js';

/**
 * Nacitanie dat s tromi stavmi, ktore ma mat kazda obrazovka:
 * nacitavanie, chyba, data. Poziadavka z CLAUDE.md.
 *
 * Prebiehajuca poziadavka sa pri odchode z obrazovky prerusi - inak by
 * odpoved dorazila do odpojeneho komponentu a Reactu by prisiel zapis
 * do niecoho, co uz neexistuje.
 */

export interface Nacitanie<T> {
  readonly data: T | undefined;
  readonly loading: boolean;
  readonly error: string | undefined;
  /** `true`, ak vyprsala relacia; obal ma na to reagovat odhlásením. */
  readonly needsLogin: boolean;
  /** Nacita znova a ukaze pritom stav nacitavania. Pre akciu pouzivatela. */
  readonly reload: () => void;
  /**
   * Nacita znova POTICHU - obrazovka zostane, ako je, a data sa vymenia
   * az ked dorazia.
   *
   * Pre pravidelne obnovovanie na pozadi: `reload` by pri kazdom kole
   * schoval celu obrazovku za stav nacitavania a vyzeralo by to ako
   * blikanie.
   */
  readonly refresh: () => void;
}

export function useApi<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[] = [],
): Nacitanie<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [pokus, setPokus] = useState(0);
  const ticho = useRef(false);

  const reload = useCallback(() => {
    ticho.current = false;
    setPokus((n) => n + 1);
  }, []);

  const refresh = useCallback(() => {
    ticho.current = true;
    setPokus((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let aktualne = true;

    const potichu = ticho.current;
    ticho.current = false;
    if (!potichu) {
      setLoading(true);
      setError(undefined);
    }

    fetcher(controller.signal)
      .then((vysledok) => {
        if (!aktualne) return;
        setData(vysledok);
        setNeedsLogin(false);
      })
      .catch((chyba: unknown) => {
        if (!aktualne || controller.signal.aborted) return;
        if (chyba instanceof ApiError && chyba.needsLogin) {
          setNeedsLogin(true);
          return;
        }
        /*
         * Pri tichom obnovovani sa chyba ukaze len vtedy, ked este
         * niet co zobrazit. Inak by kratky vypadok siete zmazal
         * obrazovku, ktora ma platne data z predosleho kola.
         */
        if (potichu && data !== undefined) return;
        setError(chyba instanceof Error ? chyba.message : 'Neznáma chyba.');
      })
      .finally(() => {
        if (aktualne && !potichu) setLoading(false);
      });

    return () => {
      aktualne = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pokus, ...deps]);

  return { data, loading, error, needsLogin, reload, refresh };
}
